import Arg from './Arg';
import { BlockTypeDescriptor, HeapTypeRef, ImmediateType } from './types';
import ImmediateEncoder from './ImmediateEncoder';
import BinaryWriter from './BinaryWriter';
import type LabelBuilder from './LabelBuilder';
import type FunctionBuilder from './FunctionBuilder';
import type ImportBuilder from './ImportBuilder';
import type FuncTypeBuilder from './FuncTypeBuilder';
import type LocalBuilder from './LocalBuilder';
import type FunctionParameterBuilder from './FunctionParameterBuilder';
import type GlobalBuilder from './GlobalBuilder';

// An instruction's immediate operands for encoding, as named fields keyed by a
// discriminated `type`. Unlike the decoder's DecodedImmediate, these carry unresolved
// builder references (labels, functions) that ImmediateEncoder resolves while writing.
export type ImmediateOperands =
  | { type: ImmediateType.BlockSignature; blockType: BlockTypeDescriptor | number }
  | { type: ImmediateType.BranchTable; defaultDepth: number; relativeDepths: number[] }
  | { type: ImmediateType.Float32; value: number }
  | { type: ImmediateType.Float64; value: number }
  | { type: ImmediateType.Function; func: FunctionBuilder | ImportBuilder }
  | { type: ImmediateType.Global; global: GlobalBuilder | ImportBuilder }
  | { type: ImmediateType.IndirectFunction; funcType: FuncTypeBuilder; tableIndex: number }
  | { type: ImmediateType.Local; local: LocalBuilder | FunctionParameterBuilder }
  | { type: ImmediateType.MemoryImmediate; alignment: number; offset: number | bigint }
  | { type: ImmediateType.RelativeDepth; label: LabelBuilder; depth: number }
  | { type: ImmediateType.VarUInt1; value: number }
  | { type: ImmediateType.VarInt32; value: number }
  | { type: ImmediateType.VarInt64; value: number | bigint }
  | { type: ImmediateType.VarUInt32; value: number }
  | { type: ImmediateType.V128Const; bytes: Uint8Array }
  | { type: ImmediateType.LaneIndex; index: number }
  | { type: ImmediateType.ShuffleMask; mask: Uint8Array }
  | { type: ImmediateType.TypeIndexField; typeIndex: number; fieldIndex: number }
  | { type: ImmediateType.TypeIndexIndex; typeIndex: number; index: number }
  | { type: ImmediateType.HeapType; heapType: HeapTypeRef }
  | { type: ImmediateType.BrOnCast; flags: number; label: LabelBuilder; heapType1: HeapTypeRef; heapType2: HeapTypeRef; depth: number };

export default class Immediate {
  readonly operands: ImmediateOperands;

  constructor(operands: ImmediateOperands) {
    Arg.notNull('operands', operands);
    this.operands = operands;
  }

  get type(): ImmediateType {
    return this.operands.type;
  }

  // The immediate's numeric operands in encoding order (type/field indices, depths, lane
  // counts), excluding builder references and byte vectors. Verification reads these
  // positionally the way the former values[] array exposed them.
  numericOperands(): number[] {
    const operands = this.operands;
    switch (operands.type) {
      case ImmediateType.BlockSignature: return typeof operands.blockType === 'number' ? [operands.blockType] : [];
      case ImmediateType.BranchTable: return [operands.defaultDepth, ...operands.relativeDepths];
      case ImmediateType.Float32: case ImmediateType.Float64: case ImmediateType.VarUInt1:
      case ImmediateType.VarInt32: case ImmediateType.VarUInt32: return [operands.value];
      case ImmediateType.VarInt64: return typeof operands.value === 'number' ? [operands.value] : [];
      case ImmediateType.IndirectFunction: return [operands.tableIndex];
      case ImmediateType.MemoryImmediate: return typeof operands.offset === 'number' ? [operands.alignment, operands.offset] : [operands.alignment];
      case ImmediateType.RelativeDepth: return [operands.depth];
      case ImmediateType.LaneIndex: return [operands.index];
      case ImmediateType.TypeIndexField: return [operands.typeIndex, operands.fieldIndex];
      case ImmediateType.TypeIndexIndex: return [operands.typeIndex, operands.index];
      case ImmediateType.HeapType: return typeof operands.heapType === 'number' ? [operands.heapType] : [];
      case ImmediateType.BrOnCast: return [operands.flags, operands.depth];
      case ImmediateType.Function: case ImmediateType.Global: case ImmediateType.Local:
      case ImmediateType.V128Const: case ImmediateType.ShuffleMask: return [];
    }
  }

  static createBlockSignature(blockType: BlockTypeDescriptor | number): Immediate {
    return new Immediate({ type: ImmediateType.BlockSignature, blockType });
  }

  static createBranchTable(defaultLabel: LabelBuilder, labels: LabelBuilder[], depth: number): Immediate {
    const relativeDepths = labels.map((label) => depth - label.block!.depth);
    const defaultDepth = depth - defaultLabel.block!.depth;
    return new Immediate({ type: ImmediateType.BranchTable, defaultDepth, relativeDepths });
  }

  static createFloat32(value: number): Immediate {
    return new Immediate({ type: ImmediateType.Float32, value });
  }

  static createFloat64(value: number): Immediate {
    return new Immediate({ type: ImmediateType.Float64, value });
  }

  static createFunction(func: FunctionBuilder | ImportBuilder): Immediate {
    return new Immediate({ type: ImmediateType.Function, func });
  }

  static createGlobal(global: GlobalBuilder | ImportBuilder): Immediate {
    return new Immediate({ type: ImmediateType.Global, global });
  }

  static createIndirectFunction(funcType: FuncTypeBuilder, tableIndex: number = 0): Immediate {
    return new Immediate({ type: ImmediateType.IndirectFunction, funcType, tableIndex });
  }

  static createLocal(local: LocalBuilder | FunctionParameterBuilder): Immediate {
    return new Immediate({ type: ImmediateType.Local, local });
  }

  static createMemoryImmediate(alignment: number, offset: number | bigint): Immediate {
    return new Immediate({ type: ImmediateType.MemoryImmediate, alignment, offset });
  }

  static createRelativeDepth(label: LabelBuilder, depth: number): Immediate {
    return new Immediate({ type: ImmediateType.RelativeDepth, label, depth });
  }

  static createVarUInt1(value: number): Immediate {
    return new Immediate({ type: ImmediateType.VarUInt1, value });
  }

  static createVarInt32(value: number): Immediate {
    return new Immediate({ type: ImmediateType.VarInt32, value });
  }

  static createVarInt64(value: number | bigint): Immediate {
    return new Immediate({ type: ImmediateType.VarInt64, value });
  }

  static createVarUInt32(value: number): Immediate {
    return new Immediate({ type: ImmediateType.VarUInt32, value });
  }

  static createV128Const(bytes: Uint8Array): Immediate {
    if (bytes.length !== 16) {
      throw new Error('V128 constant must be exactly 16 bytes.');
    }
    return new Immediate({ type: ImmediateType.V128Const, bytes });
  }

  static createLaneIndex(index: number): Immediate {
    return new Immediate({ type: ImmediateType.LaneIndex, index });
  }

  static createShuffleMask(mask: Uint8Array): Immediate {
    if (mask.length !== 16) {
      throw new Error('Shuffle mask must be exactly 16 bytes.');
    }
    return new Immediate({ type: ImmediateType.ShuffleMask, mask });
  }

  static createTypeIndexField(typeIndex: number, fieldIndex: number): Immediate {
    return new Immediate({ type: ImmediateType.TypeIndexField, typeIndex, fieldIndex });
  }

  static createTypeIndexIndex(typeIndex: number, index: number): Immediate {
    return new Immediate({ type: ImmediateType.TypeIndexIndex, typeIndex, index });
  }

  static createHeapType(heapType: HeapTypeRef): Immediate {
    return new Immediate({ type: ImmediateType.HeapType, heapType });
  }

  static createBrOnCast(flags: number, label: LabelBuilder, heapType1: HeapTypeRef, heapType2: HeapTypeRef, depth: number): Immediate {
    return new Immediate({ type: ImmediateType.BrOnCast, flags, label, heapType1, heapType2, depth });
  }

  writeBytes(writer: BinaryWriter): void {
    const operands = this.operands;
    switch (operands.type) {
      case ImmediateType.BlockSignature:
        return ImmediateEncoder.encodeBlockSignature(writer, operands.blockType);
      case ImmediateType.BranchTable:
        return ImmediateEncoder.encodeBranchTable(writer, operands.defaultDepth, operands.relativeDepths);
      case ImmediateType.Float32:
        return ImmediateEncoder.encodeFloat32(writer, operands.value);
      case ImmediateType.Float64:
        return ImmediateEncoder.encodeFloat64(writer, operands.value);
      case ImmediateType.Function:
        return ImmediateEncoder.encodeFunction(writer, operands.func);
      case ImmediateType.Global:
        return ImmediateEncoder.encodeGlobal(writer, operands.global);
      case ImmediateType.IndirectFunction:
        return ImmediateEncoder.encodeIndirectFunction(writer, operands.funcType, operands.tableIndex);
      case ImmediateType.Local:
        return ImmediateEncoder.encodeLocal(writer, operands.local);
      case ImmediateType.MemoryImmediate:
        return ImmediateEncoder.encodeMemoryImmediate(writer, operands.alignment, operands.offset);
      case ImmediateType.RelativeDepth:
        return ImmediateEncoder.encodeRelativeDepth(writer, operands.label, operands.depth);
      case ImmediateType.VarInt32:
        return ImmediateEncoder.encodeVarInt32(writer, operands.value);
      case ImmediateType.VarInt64:
        return ImmediateEncoder.encodeVarInt64(writer, operands.value);
      case ImmediateType.VarUInt1:
        return ImmediateEncoder.encodeVarUInt1(writer, operands.value);
      case ImmediateType.VarUInt32:
        return ImmediateEncoder.encodeVarUInt32(writer, operands.value);
      case ImmediateType.V128Const:
        return ImmediateEncoder.encodeV128Const(writer, operands.bytes);
      case ImmediateType.LaneIndex:
        return ImmediateEncoder.encodeLaneIndex(writer, operands.index);
      case ImmediateType.ShuffleMask:
        return ImmediateEncoder.encodeShuffleMask(writer, operands.mask);
      case ImmediateType.TypeIndexField:
        return ImmediateEncoder.encodeTypeIndexField(writer, operands.typeIndex, operands.fieldIndex);
      case ImmediateType.TypeIndexIndex:
        return ImmediateEncoder.encodeTypeIndexIndex(writer, operands.typeIndex, operands.index);
      case ImmediateType.HeapType:
        return ImmediateEncoder.encodeHeapType(writer, operands.heapType);
      case ImmediateType.BrOnCast:
        return ImmediateEncoder.encodeBrOnCast(writer, operands.flags, operands.label, operands.heapType1, operands.heapType2, operands.depth);
    }
  }

  toBytes(): Uint8Array {
    const buffer = new BinaryWriter();
    this.writeBytes(buffer);
    return buffer.toArray();
  }
}
